import { useState, useEffect, useMemo, useCallback } from 'react';
import type {
  Subscription,
  Invoice,
  Vendor,
  PaymentMethod,
  SubscriptionSnapshot,
} from '../types/subscriptionManagement';
import { enrichInvoiceCoverageFields } from '../utils/subscriptionInvoiceCoverage';

const API = '/api/subscription-mgmt';

// ─── API ↔ Frontend mappers ──────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

function mapSubFromApi(r: any): Subscription {
  return {
    id: r.subscription_id || '',
    name: r.subscription_name || '',
    vendor: r.vendor_name || '',
    department: r.department || '',
    category: r.category || '',
    owner: r.responsible_person || '',
    approvedBy: r.approver_name || '',
    billingCycle: r.billing_cycle || '',
    amount: Number(r.billing_amount) || 0,
    currency: r.currency || 'USD',
    monthlyCost: Number(r.normalized_monthly_cost) || 0,
    startDate: r.contract_start_date || '',
    renewalDate: r.renewal_date || '',
    paymentMethod: r.payment_method_name || '',
    approvedBudget: r.approved_budget || '',
    status: r.status || 'Active',
    approvalStatus: r.approval_status || 'Approved',
    notes: r.remarks || '',
  };
}

function mapSubToApi(d: Partial<Subscription>): Record<string, any> {
  const out: Record<string, any> = {};
  if (d.name !== undefined) out.subscription_name = d.name;
  if (d.vendor !== undefined) out.vendor_name = d.vendor;
  if (d.department !== undefined) out.department = d.department;
  if (d.category !== undefined) out.category = d.category;
  if (d.owner !== undefined) out.responsible_person = d.owner;
  if (d.approvedBy !== undefined) out.approver_name = d.approvedBy;
  if (d.billingCycle !== undefined) out.billing_cycle = d.billingCycle;
  if (d.amount !== undefined) out.billing_amount = d.amount;
  if (d.currency !== undefined) out.currency = d.currency;
  if (d.startDate !== undefined) out.contract_start_date = d.startDate;
  if (d.renewalDate !== undefined) out.renewal_date = d.renewalDate;
  if (d.paymentMethod !== undefined) out.payment_method_name = d.paymentMethod;
  if (d.approvedBudget !== undefined) out.approved_budget = d.approvedBudget;
  if (d.status !== undefined) out.status = d.status;
  if (d.approvalStatus !== undefined) out.approval_status = d.approvalStatus;
  if (d.notes !== undefined) out.remarks = d.notes;
  return out;
}

function mapInvFromApi(r: any, sub?: Subscription): Invoice {
  const inv: Invoice = {
    id: r.invoice_id || '',
    subscriptionId: r.subscription_id || '',
    invoiceNumber: r.invoice_number || '',
    invoiceDate: r.invoice_date || '',
    subscriptionStartDate: r.subscription_start_date || '',
    subscriptionEndDate: r.subscription_end_date || '',
    amount: Number(r.invoice_amount) || 0,
    currency: r.currency || 'USD',
    paymentMethod: r.payment_method_name || '',
    paymentStatus: r.payment_status || 'Unpaid',
    invoiceFile: r.invoice_file_url || '',
  };
  return sub ? enrichInvoiceCoverageFields(inv, sub) : inv;
}

function mapInvToApi(d: Partial<Invoice>): Record<string, any> {
  const out: Record<string, any> = {};
  if (d.subscriptionId !== undefined) out.subscription_id = d.subscriptionId;
  if (d.invoiceNumber !== undefined) out.invoice_number = d.invoiceNumber;
  if (d.invoiceDate !== undefined) out.invoice_date = d.invoiceDate;
  if (d.subscriptionStartDate !== undefined) {
    out.subscription_start_date = d.subscriptionStartDate;
  }
  if (d.subscriptionEndDate !== undefined) {
    out.subscription_end_date = d.subscriptionEndDate;
  }
  if (d.amount !== undefined) out.invoice_amount = d.amount;
  if (d.currency !== undefined) out.currency = d.currency;
  if (d.paymentMethod !== undefined) out.payment_method_name = d.paymentMethod;
  if (d.paymentStatus !== undefined) out.payment_status = d.paymentStatus;
  if (d.invoiceFile !== undefined) out.invoice_file_url = d.invoiceFile;
  return out;
}

/* eslint-enable @typescript-eslint/no-explicit-any */

function uniqueSortedStrings(values: (string | undefined)[]): string[] {
  const s = new Set<string>();
  for (const v of values) {
    const t = (v || '').trim();
    if (t) s.add(t);
  }
  return [...s].sort((a, b) => a.localeCompare(b));
}

// ─── Fetch helper ────────────────────────────────────────────────────────────

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseSubscriptionDataResult {
  subscriptions: Subscription[];
  invoices: Invoice[];
  /** Distinct vendor names from subscriptions (no separate vendor table). */
  vendors: Vendor[];
  /** Distinct payment method names from subscriptions. */
  paymentMethods: PaymentMethod[];
  /** Google Sheet Lookups tab columns → values (drives modal dropdowns when set). */
  lookupColumns: Record<string, string[]>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  createSubscription: (data: Partial<Subscription>) => Promise<Subscription>;
  updateSubscription: (id: string, data: Partial<Subscription>) => Promise<Subscription>;
  deleteSubscription: (id: string) => Promise<void>;
  createInvoice: (data: Partial<Invoice>) => Promise<Invoice>;
  updateInvoice: (id: string, data: Partial<Invoice>) => Promise<Invoice>;
  deleteInvoice: (id: string) => Promise<void>;
  uploadInvoiceFile: (id: string, file: File) => Promise<string>;
  refresh: () => void;
}

export function useSubscriptionData(): UseSubscriptionDataResult {
  const [snapshot, setSnapshot] = useState<SubscriptionSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  // ── Load snapshot ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (tick === 0) {
      setLoading(true);
      setError(null);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    apiFetch<any>(`${API}/snapshot`)
      .then(data => {
        if (cancelled) return;
        const subs: Subscription[] = (data.subscriptions?.rows ?? []).map(
          mapSubFromApi,
        );
        const subById = new Map(subs.map(s => [s.id, s]));
        const invs: Invoice[] = (data.invoices?.rows ?? []).map((r: Record<string, unknown>) =>
          mapInvFromApi(r, subById.get(String(r.subscription_id || ''))),
        );
        const lookup = (data.lookup_columns ?? {}) as Record<string, string[]>;
        const fromSheetVendors = lookup['Vendor'] ?? [];
        const fromSheetPaymentMethods = lookup['Payment Method'] ?? [];
        setSnapshot({
          subscriptions: subs,
          invoices: invs,
          lookupColumns: lookup,
          vendors: uniqueSortedStrings([
            ...subs.map(s => s.vendor),
            ...fromSheetVendors,
          ]).map(name => ({
            id: name,
            name,
            category: '',
          })),
          paymentMethods: uniqueSortedStrings([
            ...subs.map(s => s.paymentMethod),
            ...fromSheetPaymentMethods,
          ]).map(name => ({
            id: name,
            name,
            type: '',
          })),
        });
      })
      .catch(err => {
        if (!cancelled) setError(String(err?.message ?? err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tick]);

  // ── Derived lists ────────────────────────────────────────────────────────
  const subscriptions = useMemo(() => snapshot?.subscriptions ?? [], [snapshot]);
  const invoices = useMemo(() => snapshot?.invoices ?? [], [snapshot]);
  const vendors = useMemo(() => snapshot?.vendors ?? [], [snapshot]);
  const paymentMethods = useMemo(() => snapshot?.paymentMethods ?? [], [snapshot]);
  const lookupColumns = useMemo(
    () => snapshot?.lookupColumns ?? {},
    [snapshot],
  );

  // ── Mutation helpers ─────────────────────────────────────────────────────
  const mutate = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setSaving(true);
    try {
      const result = await fn();
      refresh();
      return result;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  // ── Subscription CRUD ────────────────────────────────────────────────────
  const createSubscription = useCallback(
    (data: Partial<Subscription>) =>
      mutate(async () => {
        const raw = await apiFetch<Record<string, unknown>>(`${API}/subscriptions`, {
          method: 'POST',
          body: JSON.stringify(mapSubToApi(data)),
        });
        return mapSubFromApi(raw);
      }),
    [mutate]
  );

  const updateSubscription = useCallback(
    (id: string, data: Partial<Subscription>) =>
      mutate(async () => {
        const raw = await apiFetch<Record<string, unknown>>(`${API}/subscriptions/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify(mapSubToApi(data)),
        });
        return mapSubFromApi(raw);
      }),
    [mutate]
  );

  const deleteSubscription = useCallback(
    (id: string) =>
      mutate(async () => {
        await apiFetch<{ ok: boolean }>(`${API}/subscriptions/${encodeURIComponent(id)}`, {
          method: 'DELETE',
        });
      }),
    [mutate]
  );

  // ── Invoice CRUD ─────────────────────────────────────────────────────────
  const createInvoice = useCallback(
    (data: Partial<Invoice>) =>
      mutate(async () => {
        const sid = String(data.subscriptionId || '').trim();
        const sub = subscriptions.find(s => s.id === sid);
        const raw = await apiFetch<Record<string, unknown>>(`${API}/invoices`, {
          method: 'POST',
          body: JSON.stringify(mapInvToApi(data)),
        });
        return mapInvFromApi(raw, sub);
      }),
    [mutate, subscriptions],
  );

  const updateInvoice = useCallback(
    (id: string, data: Partial<Invoice>) =>
      mutate(async () => {
        const sid = String(data.subscriptionId || '').trim();
        const sub = subscriptions.find(s => s.id === sid);
        const raw = await apiFetch<Record<string, unknown>>(`${API}/invoices/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify(mapInvToApi(data)),
        });
        return mapInvFromApi(raw, sub);
      }),
    [mutate, subscriptions],
  );

  const deleteInvoice = useCallback(
    (id: string) =>
      mutate(async () => {
        await apiFetch<{ ok: boolean }>(`${API}/invoices/${encodeURIComponent(id)}`, {
          method: 'DELETE',
        });
      }),
    [mutate]
  );

  const uploadInvoiceFile = useCallback(
    (id: string, file: File) =>
      mutate(async () => {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${API}/invoices/${encodeURIComponent(id)}/upload`, {
          method: 'POST',
          body: form,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Upload failed');
        }
        const data = await res.json();
        return data.invoice_file_url as string;
      }),
    [mutate]
  );

  return {
    subscriptions,
    invoices,
    vendors,
    paymentMethods,
    lookupColumns,
    loading,
    saving,
    error,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    uploadInvoiceFile,
    refresh,
  };
}
